<template>
  <div class="pp-root">

    <!-- Header -->
    <header class="pp-header">
      <RouterLink to="/" class="pp-logo">
        <img src="/new-logo.png" class="pp-logo-mark" alt="">
        <span class="pp-logo-text">Maths Eleven Plus</span>
      </RouterLink>
    </header>

    <!-- Title -->
    <div class="pp-title-block">
      <p class="pp-eyebrow">Almost There</p>
      <h1 class="pp-h1">Choose your plan</h1>
      <p class="pp-sub">Start free — upgrade any time. No contracts.</p>
    </div>

    <!-- Plan cards -->
    <div class="pp-grid">
      <!-- Loading skeletons -->
      <template v-if="plansLoading">
        <div v-for="i in 3" :key="i" class="pp-card pp-card-skeleton" />
      </template>

      <!-- API-driven plan cards -->
      <template v-else>
        <button
          v-for="plan in plans"
          :key="plan.key"
          type="button"
          class="pp-card"
          :class="{
            'pp-card-featured':  plan.highlight,
            'pp-card-disabled':  selecting && selecting !== plan.key,
          }"
          :disabled="!!selecting"
          @click="selectPlan(plan.key)"
        >
          <div v-if="plan.highlight" class="pp-card-badge">Most Popular</div>

          <div class="pp-card-name">{{ plan.name }}</div>
          <div class="pp-card-price">{{ plan.price }}</div>
          <div class="pp-card-period">{{ plan.period }}</div>

          <ul class="pp-card-features">
            <li v-for="feature in plan.features" :key="feature">{{ feature }}</li>
          </ul>

          <div class="pp-card-cta">
            <template v-if="selecting === plan.key">
              <span class="pp-cta-spinner"></span> Setting up…
            </template>
            <template v-else>{{ plan.cta }}</template>
          </div>
        </button>
      </template>
    </div>

    <p v-if="checkoutError" class="pp-error">{{ checkoutError }}</p>
    <p class="pp-footnote">No credit card required for the free plan &middot; Cancel any time</p>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { useUserStore } from '@/stores/userStore';
import * as authService from '@/services/authService';
import { accountApi } from '@/services/accountApi';
import { getPlans, PLAN_ICONS, PLAN_HIGHLIGHT, PLAN_CTAS, formatPrice, formatPeriod } from '@/services/planService';
import { useAnalytics } from '@/composables/useAnalytics';

const router = useRouter();
const { session } = useAuth();
const userStore = useUserStore();
const { track } = useAnalytics();

const apiPlans = ref({});
const plansLoading = ref(true);
const selecting = ref(null);       // planKey being confirmed, shows spinner on that card
const checkoutError = ref('');

/** Plan cards sorted by tier, driven by the API. */
const plans = computed(() =>
  Object.values(apiPlans.value)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map(p => ({
      key:       p.planId,
      name:      p.name,
      price:     formatPrice(p),
      period:    formatPeriod(p),
      cta:       PLAN_CTAS[p.planId] ?? `Choose ${p.name}`,
      highlight: PLAN_HIGHLIGHT === p.planId,
      features:  p.displayFeatures ?? [],
    }))
);

onMounted(async () => {
  try {
    const map = await getPlans();
    if (Object.keys(map).length) apiPlans.value = map;
  } finally {
    plansLoading.value = false;
  }
});

async function selectPlan(planKey) {
  const uid = session.value?.uid;
  checkoutError.value = '';

  // Free plan — proceed immediately
  if (planKey === 'free') {
    if (uid) authService.setPlan(uid, 'free');
    track('plan_selected', { plan: 'free', at_signup: true });
    router.push('/welcome');
    return;
  }

  // Paid plan — redirect to Stripe Checkout
  // The webhook activates the subscription; no local state change needed here.
  selecting.value = planKey;
  try {
    // Ensure the account record exists before creating the Checkout Session
    await accountApi.createAccount({
      uid,
      email: session.value.email,
      name:  session.value.name,
    });

    const origin = window.location.origin;
    const { url } = await accountApi.createCheckoutSession({
      uid,
      plan:       planKey,
      successUrl: `${origin}/welcome?stripe_success=1`,
      cancelUrl:  `${origin}/payment`,
    });

    track('plan_selected', { plan: planKey, at_signup: true });
    window.location.href = url;   // hand off to Stripe-hosted checkout page
  } catch (e) {
    console.error('[PaymentPage] Stripe checkout failed:', e.message);
    checkoutError.value = 'Could not start checkout — please try again or choose the free plan.';
    selecting.value = null;
  }
  // Don't reset selecting.value on success — we're navigating away
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=Jost:wght@300;400;500;600;700&display=swap');

.pp-root {
  min-height: 100vh;
  background: #0D1628;
  font-family: 'Jost', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px 60px;
  background-image:
    radial-gradient(circle at 30% 60%, rgba(184,144,42,0.06) 0%, transparent 50%),
    url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23B8902A' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E");
}

.pp-header {
  display: flex;
  justify-content: center;
  padding: 32px 0 20px;
  width: 100%;
}

.pp-logo {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}
.pp-logo-mark {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  flex-shrink: 0;
}
.pp-logo-text {
  font-family: 'Cormorant Garamond', serif;
  font-size: 21px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.02em;
}

.pp-title-block {
  text-align: center;
  margin-bottom: 44px;
}
.pp-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #B8902A;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.pp-eyebrow::before, .pp-eyebrow::after {
  content: '';
  width: 28px;
  height: 1px;
  background: #B8902A;
  flex-shrink: 0;
}
.pp-h1 {
  font-family: 'Cormorant Garamond', serif;
  font-size: 48px;
  font-weight: 600;
  color: white;
  line-height: 1.1;
  margin-bottom: 12px;
  letter-spacing: -0.01em;
}
.pp-sub {
  font-size: 15px;
  color: rgba(255,255,255,0.75);
  font-weight: 400;
}

/* Cards grid */
.pp-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 1100px;
}

.pp-card {
  background: #F9F6EF;
  border-radius: 8px;
  padding: 36px 32px;
  text-align: left;
  border: 1px solid rgba(184,144,42,0.15);
  cursor: pointer;
  position: relative;
  transition: all 0.25s;
  box-shadow: 0 20px 40px rgba(0,0,0,0.25);
}
.pp-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 32px 56px rgba(0,0,0,0.35);
  border-color: rgba(184,144,42,0.4);
}
.pp-card-featured {
  border-color: #B8902A;
  background: #fff;
}
.pp-card-featured:hover {
  border-color: #d4a843;
}

.pp-card-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #B8902A;
  color: #0D1628;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 16px;
  border-radius: 20px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
}

.pp-card-name {
  font-family: 'Cormorant Garamond', serif;
  font-size: 26px;
  font-weight: 600;
  color: #0D1628;
  margin-bottom: 10px;
}
.pp-card-price {
  font-family: 'Cormorant Garamond', serif;
  font-size: 52px;
  font-weight: 500;
  color: #B8902A;
  line-height: 1;
  margin-bottom: 4px;
}
.pp-card-period {
  font-size: 14px;
  color: #777;
  font-weight: 400;
  margin-bottom: 24px;
}

.pp-card-features {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.pp-card-features li {
  font-size: 14px;
  color: #444;
  padding: 8px 0;
  border-bottom: 1px solid #ede9e0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-weight: 400;
  line-height: 1.5;
}
.pp-card-features li::before {
  content: '✓';
  color: #B8902A;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.pp-card-cta {
  display: block;
  text-align: center;
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 1px solid rgba(184,144,42,0.35);
  color: #B8902A;
  transition: all 0.2s;
}
.pp-card-featured .pp-card-cta {
  background: #B8902A;
  color: #0D1628;
  border-color: #B8902A;
}
.pp-card:hover .pp-card-cta {
  background: #B8902A;
  color: #0D1628;
  border-color: #B8902A;
}

.pp-error {
  margin-top: 20px;
  padding: 12px 20px;
  background: rgba(239,68,68,0.12);
  border: 1px solid rgba(239,68,68,0.35);
  border-radius: 8px;
  font-size: 13px;
  color: #fca5a5;
  max-width: 860px;
  width: 100%;
  text-align: center;
}

.pp-footnote {
  margin-top: 28px;
  font-size: 13px;
  color: rgba(255,255,255,0.65);
  text-align: center;
  font-weight: 400;
}

.pp-card-skeleton {
  min-height: 340px;
  background: rgba(255,255,255,0.04);
  border-color: rgba(184,144,42,0.08) !important;
  cursor: default;
  animation: pp-pulse 1.4s ease-in-out infinite;
}
@keyframes pp-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.45; }
}

.pp-card-disabled {
  opacity: 0.4;
  pointer-events: none;
}

.pp-cta-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 2px solid rgba(184,144,42,0.4);
  border-top-color: #B8902A;
  border-radius: 50%;
  animation: pp-spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 4px;
}
.pp-card-featured .pp-cta-spinner {
  border-color: rgba(13,22,40,0.3);
  border-top-color: #0D1628;
}
@keyframes pp-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 800px) {
  .pp-grid { grid-template-columns: 1fr; max-width: 420px; }
  .pp-h1 { font-size: 36px; }
}
</style>
